import { ArrowRight, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { getTranslationFn } from "../../i18n";
import { getLocale, type Locale } from "../../lib/locale";
import { cn } from "../../lib/utils";
import * as Paths from "../../paths";
import { Button } from "../ui/button";
import { MarketingFooter } from "./marketing-footer";

interface MarketingLayoutProps {
  /** Explicit locale (used by landing page routes). When omitted, reads from the shared locale resolver. */
  locale?: Locale;
  children: React.ReactNode;
  /** Nav items displayed in the header. Defaults to landing-page sections. */
  navItems?: { label: string; href: string }[];
}

export function MarketingLayout({
  locale: localeProp,
  children,
  navItems: navItemsProp,
}: MarketingLayoutProps) {
  const location = useLocation();
  const locale = localeProp ?? getLocale();
  const t = getTranslationFn(locale);

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if we're on a page with dedicated locale routes
  const isLandingPage =
    location.pathname === "/" || location.pathname === "/en";

  const landingBase = locale === "en" ? "/en" : "/";
  const anchorPrefix = isLandingPage ? "" : landingBase;
  const pricingPath = locale === "en" ? "/en/pricing" : Paths.pricing();
  const securityPath = locale === "en" ? "/en/security" : Paths.security();

  const defaultNavItems = [
    {
      label: t("Landing.nav.howItWorks"),
      href: `${anchorPrefix}#use-cases`,
    },
    { label: t("Landing.nav.security"), href: securityPath },
  ];

  const navItems = navItemsProp ?? defaultNavItems;
  return (
    <div className="flex min-h-screen flex-col w-full overflow-x-clip bg-white font-sans selection:bg-brand-100">
      {/* Navigation */}
      <header
        className={cn(
          "fixed w-full top-0 z-50 transition-all duration-300 border-b border-transparent",
          isScrolled
            ? "bg-white/95 backdrop-blur-md border-slate-100 shadow-sm py-3"
            : "bg-white py-5"
        )}
      >
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link
            to={locale === "en" ? "/en" : "/"}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img
              src="/assets/logo.svg"
              alt="AnyLawyer Logo"
              className="w-[140px]"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to={pricingPath}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {t("Landing.nav.pricing")}
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Button
              asChild
              className="rounded-full px-6 bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-600/20 hover:shadow-brand-600/30 transition-all"
            >
              <Link to={Paths.app()}>
                {t("Landing.nav.goToApp")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b shadow-lg p-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-base font-medium text-slate-600 py-2 border-b border-slate-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to={pricingPath}
              className="text-base font-medium text-slate-600 py-2 border-b border-slate-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("Landing.nav.pricing")}
            </Link>
            <div className="flex flex-col gap-3 mt-2">
              <Button
                asChild
                className="w-full justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700"
              >
                <Link to={Paths.app()} onClick={() => setMobileMenuOpen(false)}>
                  {t("Landing.nav.goToApp")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </header>

      <main key={locale} className="flex-1 pt-20">
        {children}
      </main>

      <MarketingFooter locale={locale} />
    </div>
  );
}
